import jwtDecode from 'jwt-decode';

function isSalonActive(salon) {
  return salon.eliminato == null || +salon.eliminato === 0;
}

function countUniqueActiveSalons(salons) {
  const active = (salons || []).filter(isSalonActive);
  return new Map(active.map((s) => [String(s.idsalone), s])).size;
}

/** Salone operativo nel token ≠ account credenziale (tipico titolare multi-salone su sede figlia). */
export function hasCredentialOperationalMismatch(token) {
  if (!token) {
    return false;
  }
  try {
    const decoded = jwtDecode(token);
    if (decoded.role == 1 || decoded.role === '1') {
      return false;
    }
    const cred = decoded.credentialSaloneId?.toString();
    const op = decoded.idSalone?.toString();
    return !!(cred && op && cred !== op);
  } catch {
    return false;
  }
}

/** Avvertenza cambio password: multi-salone / credenziale titolare, non salone singolo né staff. */
export function shouldShowMultiSalonPasswordNotice(salons) {
  if (typeof localStorage !== 'undefined' && localStorage.getItem('staff')) {
    return false;
  }
  const list = salons || [];
  const uniqueActiveCount = countUniqueActiveSalons(list);
  const activeToken = list.find((s) => s.isActive)?.token;
  const credentialMismatch = hasCredentialOperationalMismatch(activeToken);

  if (uniqueActiveCount <= 1) {
    return credentialMismatch;
  }

  return (
    credentialMismatch ||
    list.some((salon) => isSalonActive(salon) && hasCredentialOperationalMismatch(salon.token))
  );
}

/** Token business senza credentialSaloneId (pre-migrazione multi-salone). */
export function isLegacyBusinessSalonToken(token) {
  if (!token) {
    return false;
  }
  try {
    const decoded = jwtDecode(token);
    const role = decoded?.role;
    if (role == 1 || role === '1') {
      return false;
    }
    return !decoded.credentialSaloneId;
  } catch {
    return true;
  }
}

/** Più di un salone e almeno un token senza credentialSaloneId → nuovo login (migrazione token). */
export function multiSalonHasLegacyTokens(salons) {
  const active = (salons || []).filter(isSalonActive);
  if (active.length <= 1) {
    return false;
  }
  return active.some((salon) => isLegacyBusinessSalonToken(salon.token));
}

/** Incrementare dopo fix lista multi-salone: evita di ri-chiedere il login a chi ha già rifatto accesso. */
export const SALONS_LIST_MIGRATION_VERSION = '1';

export function markSalonsListMigrationComplete() {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem('salonsListMigrationVersion', SALONS_LIST_MIGRATION_VERSION);
}

function decodeBusinessToken(token) {
  if (!token) {
    return null;
  }
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
}

/**
 * tokenLogin non restituisce tutti i figli: serve un nuovo login per ripopolare localStorage.
 * Copre token legacy, lista troncata e account admin con un solo salone salvato per errore.
 */
export function needsSalonsListReLogin(salons) {
  if (typeof localStorage !== 'undefined' && localStorage.getItem('staff')) {
    return false;
  }
  if (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('salonsListMigrationVersion') === SALONS_LIST_MIGRATION_VERSION
  ) {
    return false;
  }

  const list = (salons || []).filter(isSalonActive);
  if (!list.length) {
    return false;
  }

  if (multiSalonHasLegacyTokens(list)) {
    return true;
  }

  const active = list.find((s) => s.isActive) || list[0];
  const token = active?.token;

  if (isLegacyBusinessSalonToken(token)) {
    return true;
  }

  const uniqueCount = countUniqueActiveSalons(list);
  if (uniqueCount !== 1) {
    return false;
  }

  const decoded = decodeBusinessToken(token);
  if (hasCredentialOperationalMismatch(token)) {
    return true;
  }
  if (+active?.isSuperAdmin === 1 || decoded?.isSuperAdmin == 1) {
    return true;
  }

  return false;
}
